'use client';
import React, { useRef, useEffect } from 'react';

const steps = [
  {
    number: '01',
    title: 'Choose Your Product',
    description: 'Upload your product details, brand guidelines, and target audience. Our system maps the perfect creator persona and visual style.',
    icon: '📦',
    color: 'from-violet-500/20 to-purple-600/10',
    borderColor: 'border-violet-500/30',
    dotColor: 'bg-violet-500',
  },
  {
    number: '02',
    title: 'Generate Creator Content',
    description: 'Select from our proven UGC workflows and AI systems. Generate influencer-style ads, product demos, and social-first visuals in seconds.',
    icon: '⚡',
    color: 'from-pink-500/20 to-rose-600/10',
    borderColor: 'border-pink-500/30',
    dotColor: 'bg-pink-500',
  },
  {
    number: '03',
    title: 'Launch Social-Ready Campaigns',
    description: 'Export platform-optimized content for TikTok, Instagram, YouTube, and more. Ready to publish, ready to convert.',
    icon: '🚀',
    color: 'from-cyan-500/20 to-teal-600/10',
    borderColor: 'border-cyan-500/30',
    dotColor: 'bg-cyan-500',
  },
];

export default function HowItWorks() {
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('step-active');
          }
        });
      },
      { threshold: 0.15 }
    );
    stepRefs.current.forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative bg-[#080810] py-28 px-6 md:px-12 overflow-hidden">
      {/* Ambient glow */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(236,72,153,0.06) 0%, transparent 70%)', filter: 'blur(40px)' }}
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center max-w-xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-500/20 bg-cyan-500/5 mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
            <span className="font-sans text-xs text-cyan-300 tracking-wide">How It Works</span>
          </div>
          <h2 className="font-sans font-bold text-4xl md:text-5xl text-white leading-tight mb-4">
            From idea to campaign in{' '}
            <span className="bg-gradient-to-r from-cyan-400 to-violet-400 bg-clip-text text-transparent">
              three steps.
            </span>
          </h2>
          <p className="font-sans text-lg text-white/40">
            No shoots. No talent. No waiting. Just results.
          </p>
        </div>

        {/* Steps — horizontal on desktop */}
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {/* Connecting line — desktop */}
          <div className="hidden md:block absolute top-12 left-[16.67%] right-[16.67%] h-px bg-gradient-to-r from-violet-500/30 via-pink-500/30 to-cyan-500/30 z-0" />

          {steps.map((step, i) => (
            <div
              key={step.number}
              ref={(el) => { stepRefs.current[i] = el; }}
              className="step-card relative z-10"
              style={{ transitionDelay: `${i * 0.15}s` }}
            >
              {/* Step number circle */}
              <div className="flex items-center gap-4 mb-6">
                <div className={`relative w-12 h-12 rounded-full border ${step.borderColor} bg-gradient-to-br ${step.color} flex items-center justify-center`}>
                  <span className="font-sans font-bold text-sm text-white">{step.number}</span>
                  <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${step.dotColor} border-2 border-[#080810]`} />
                </div>
                <div className="flex-1 h-px bg-white/5 md:hidden" />
              </div>

              {/* Card */}
              <div className={`rounded-2xl border ${step.borderColor} bg-gradient-to-br ${step.color} p-6 md:p-8 h-full`}>
                <div className="text-3xl mb-4">{step.icon}</div>
                <h3 className="font-sans font-semibold text-xl text-white mb-3">{step.title}</h3>
                <p className="font-sans text-sm text-white/45 leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <a
            href="#pricing"
            className="inline-flex items-center gap-2 font-sans text-sm text-white/50 hover:text-white transition-colors duration-200"
          >
            Start your first campaign today
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 7H11M8 4L11 7L8 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
      </div>

      <style jsx>{`
        .step-card {
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .step-active {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </section>
  );
}
